import React, { useMemo } from 'react';
import {
  ImageBackground,
  TouchableOpacity,
  View,
  type ImageSourcePropType,
} from 'react-native';
import { Text } from '~/components';

import bannerTakesCare from '~/assets/img/home/banner-takes-care.png';
import bannerAuto from '~/assets/img/home/banner-auto.png';
import bannerIncomeProtection from '~/assets/img/home/banner-income-protection.png';
import bannerBagProtection from '~/assets/img/home/banner-bag-protection.png';
import ArrowRight from '~/assets/svgs/arrow-right.svg';
import {
  HiredInsuranceProductType,
  defaultHiredInsuranceStatusProps,
  type ContractedService,
} from '~/domain/Assegurou/contractedServices';
import useAppNavigation from '~/hooks/useAppNavigation';
import colors from '~/assets/themes/colors';
import { formatDate } from '~/utils/date';
import { twMerge } from 'tailwind-merge';

interface Props {
  data: ContractedService;
}

interface ServiceProps {
  banner: ImageSourcePropType;
  routeToNavigate:
    | 'MarisaServicos_HiredInsuranceDetailsAuto'
    | 'MarisaServicos_HiredInsuranceDetailsTakesCare'
    | 'MarisaServicos_HiredInsuranceDetailsBagProtection'
    | 'MarisaServicos_HiredInsuranceDetailsIncomeProtection';
}

type ServicePropsByCode = {
  [HiredInsuranceProductType.MARISA_AUTO]: ServiceProps;
  [HiredInsuranceProductType.MARISA_TAKES_CARE]: ServiceProps;
  [HiredInsuranceProductType.BAG_PROTECTION]: ServiceProps;
  [HiredInsuranceProductType.INCOME_PROTECTION]: ServiceProps;
};

const servicePropsByCode: ServicePropsByCode = {
  [HiredInsuranceProductType.MARISA_AUTO]: {
    banner: bannerAuto,
    routeToNavigate: 'MarisaServicos_HiredInsuranceDetailsAuto',
  } as ServiceProps,
  [HiredInsuranceProductType.MARISA_TAKES_CARE]: {
    banner: bannerTakesCare,
    routeToNavigate: 'MarisaServicos_HiredInsuranceDetailsTakesCare',
  },
  [HiredInsuranceProductType.BAG_PROTECTION]: {
    banner: bannerBagProtection,
    routeToNavigate: 'MarisaServicos_HiredInsuranceDetailsBagProtection',
  },
  [HiredInsuranceProductType.INCOME_PROTECTION]: {
    banner: bannerIncomeProtection,
    routeToNavigate: 'MarisaServicos_HiredInsuranceDetailsIncomeProtection',
  },
};

const ServiceCard: React.FC<Props> = ({ data }) => {
  const { navigate } = useAppNavigation();

  const serviceProps = useMemo(
    () => servicePropsByCode[data.productCode],
    [data.productCode]
  );

  const statusProps = useMemo(
    () =>
      defaultHiredInsuranceStatusProps[
        data.cancellationDate ? 'canceled' : data.status ?? 'approved'
      ],
    [data.cancellationDate, data.status]
  );

  return (
    <View className="rounded-lg overflow-hidden border border-gray-100 sm:max-w-[400px] mb-4">
      <ImageBackground
        source={serviceProps.banner}
        className={`bg-light-100 p-3 h-[180px]`}
      >
        <View className=" flex-1 justify-end">
          <Text className={`text-light-100 text-lg font-bold mt-14`}>
            {data.productName}
          </Text>

          <Text className={`text-light-100 mt-1 text-sm`}>
            {data.productDescription}
          </Text>
        </View>
      </ImageBackground>
      <View className="bg-light-100 p-4 flex-row items-center">
        <View
          className={twMerge('px-2 py-1 rounded-xl mr-2', statusProps.bgColor)}
        >
          <Text className="text-light-100  text-xs ">{statusProps.label}</Text>
        </View>
        <Text className="text-gray-400 text-xs">
          {formatDate(data.saleDate)}
        </Text>
        <TouchableOpacity
          className="p-1 bg-primary-600 rounded-full ml-auto"
          activeOpacity={0.6}
          onPress={() =>
            navigate(serviceProps.routeToNavigate, {
              id: data.certificateCode,
            })
          }
        >
          <ArrowRight color={colors.light[100]} />
        </TouchableOpacity>
      </View>
    </View>
  );
};

export default ServiceCard;
